import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Segment } from '~/Helpers/Segment.helper';
import { useMediaQuery, useTheme } from '@mui/material';
import { useLanguage, useNavigation, useLocalStorage } from '~/Hooks';
import { selectCurrentProvider } from '~/store/provider';
import { UserType } from '~/Models/DTO/User.model';
import { useRequestOrgRoles, useRequestRoles, useRequestUser } from '~/requests/userService';

export const useUserWithoutContext = () => {
  const theme = useTheme();
  const { currentLanguage } = useLanguage();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { history } = useNavigation();
  const { accessToken } = useLocalStorage();
  const {
    refetchUser,
    user,
    userLoading,
    hasUserLoaded,
    userError,
    updatingError,
  } = useRequestUser();
  const {
    roles,
    refetchRoles,
    hasRolesLoaded,
    roleLoading,
    updateRoles,
    roleUpdateResponse,
  } = useRequestRoles();
  const { data: orgRoles } = useRequestOrgRoles({}, { id: user?.uuid });

  // selectors
  const isAuthenticated = !!accessToken;
  const currentProvider = useSelector(selectCurrentProvider);

  // constants
  const role = useMemo(
    () =>
      orgRoles?.filter((role) => role.organisationId === currentProvider?.organisationId)?.[0]
        ?.name || roles.filter((role) => role.providerId === currentProvider?.uuid)?.[0]?.name,
    [roles, currentProvider?.uuid, orgRoles, currentProvider?.organisationId],
  );

  const roleWithoutOrg = useMemo(
    () => roles.filter((role) => role.providerId === currentProvider?.uuid)?.[0]?.name,
    [roles, currentProvider?.uuid],
  );

  useEffect(() => {
    if (isAuthenticated && !user && !userLoading) {
      refetchUser();
    }
    if (user && currentProvider) {
      Segment.identify(user.uuid ?? '', {
        id: user.uuid,
        email: user.email,
        country: user.country,
        phone: user.phone_number,
        firstname: user.first_name,
        lastname: user.last_name,
        language: currentLanguage,
        device: isMobile ? 'Mobile' : 'Desktop',
        isSuperAdmin: String(user.utype === UserType.SUPER_ADMIN),
        isPartnergramSupportAgent: String(user.utype === UserType.PARTNERGRAM),
        providerId: currentProvider.uuid,
        accessType: currentProvider.accessType,
      });
    }
  }, [user, currentProvider]);

  useEffect(() => {
    if (user && currentProvider) {
      // this is to make sure HotJar works correctly.
      setTimeout(() => {
        Segment.identify(user.uuid ?? '', {
          id: user?.uuid,
          email: user?.email,
          country: user?.country,
          phone: user?.phone_number,
          firstname: user?.first_name,
          lastname: user?.last_name,
          language: currentLanguage,
          device: isMobile ? 'Mobile' : 'Desktop',
          isSuperAdmin: String(user?.utype === UserType.SUPER_ADMIN),
          isPartnergramSupportAgent: String(user?.utype === UserType.PARTNERGRAM),
          providerId: currentProvider?.uuid,
          accessType: currentProvider?.accessType,
        });
      }, 250);
    }
  }, [history.location.pathname, isMobile]);

  useEffect(() => {
    if (!hasRolesLoaded && user?.uuid) {
      refetchRoles();
    }
  }, [user?.uuid, currentProvider?.uuid]);

  return {
    currentUser: user,
    isCurrentUserLoading: userLoading || roleLoading,
    isAuthenticated,
    forceRefreshUser: refetchUser,
    DANGEROUSLY_isSuperAdmin: user?.utype === UserType.SUPER_ADMIN,
    DANGEROUSLY_isPartnergramSupportAgent: user?.utype === UserType.PARTNERGRAM,
    role,
    roleWithoutOrg,
    hasUserLoaded,
    hasRolesLoaded,
    updateRoles,
    roleUpdateResponse,
    userError: userError || updatingError,
  };
};

interface UserContextWrapperProps {
  children: React.ReactNode;
}

type UserContextType = ReturnType<typeof useUserWithoutContext>;

const UserContext = createContext<UserContextType>({} as UserContextType);

export const UserContextWrapper = (props: UserContextWrapperProps) => {
  const store = useUserWithoutContext();
  const { children } = props;

  return <UserContext.Provider value={store}>{children}</UserContext.Provider>;
};

export const useUser = () => useContext(UserContext);
